import { useEffect, useRef, useState, type PointerEvent } from 'react';
import { AlertTriangle, ChevronLeft, ChevronRight, Eye, Loader2 } from 'lucide-react';

import { fetchRedactionPageImage } from '../../lib/redactionApi';
import type { RedactionBox, RedactionPageGeometry } from '../../types/redaction';

interface RedactionPageViewProps {
  recordId: string;
  pages: RedactionPageGeometry[];
  pagesWithImages: number[];
  pagesOcrd?: number[];
  /** Candidate boxes for every page; the component shows those on the current page. */
  boxes: RedactionBox[];
  /** Boxes the reviewer drew by hand. */
  drawnBoxes: RedactionBox[];
  page: number;
  onPageChange: (page: number) => void;
  onDrawBox: (box: RedactionBox) => void;
}

const MIN_BOX_POINTS = 4;

/**
 * The page as it will be read, with the proposed boxes drawn over it.
 *
 * Boxes are positioned as percentages of the page's own point dimensions, so
 * the overlay stays aligned at any render or display size.
 *
 * Hovering a box makes it transparent. Dragging on the page draws a new one:
 * that is how a face, a signature, or text inside an image is marked when
 * nothing automatic can name it.
 */
export default function RedactionPageView({
  recordId,
  pages,
  pagesWithImages,
  pagesOcrd = [],
  boxes,
  drawnBoxes,
  page,
  onPageChange,
  onDrawBox,
}: RedactionPageViewProps) {
  // Keyed by the page it was fetched for, so loading and error states are
  // derived rather than set alongside the fetch. A result that arrives after
  // the reviewer has moved on is then simply ignored.
  const [loaded, setLoaded] = useState<{ key: string; url: string } | null>(null);
  const [failedKey, setFailedKey] = useState<string | null>(null);
  // Null means show the current page. A string is the value being typed, so
  // "12" does not jump to page 1 on the way through.
  const [pageDraft, setPageDraft] = useState<string | null>(null);
  const [draftBox, setDraftBox] = useState<RedactionBox | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const dragOrigin = useRef<{ x: number; y: number } | null>(null);
  const key = `${recordId}:${page}`;
  const lastPage = pages.length;
  const displayedPage = pageDraft ?? String(page + 1);

  useEffect(() => {
    dragOrigin.current = null;
    setDraftBox(null);
  }, [page]);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    fetchRedactionPageImage(recordId, page)
      .then((url) => {
        objectUrl = url;
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        setLoaded({ key: `${recordId}:${page}`, url });
      })
      .catch(() => {
        if (!cancelled) setFailedKey(`${recordId}:${page}`);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [recordId, page]);

  const imageUrl = loaded?.key === key ? loaded.url : null;
  const hasError = failedKey === key;
  const isLoading = !imageUrl && !hasError;

  const geometry = pages.find((candidate) => candidate.number === page);
  const pageBoxes = boxes.filter((box) => box.page === page);
  const pageDrawn = drawnBoxes.filter((box) => box.page === page);
  const hasImageContent = pagesWithImages.includes(page);
  const wasOcrd = pagesOcrd.includes(page);

  const pointOnPage = (clientX: number, clientY: number) => {
    const overlay = overlayRef.current;
    if (!overlay || !geometry) return null;
    const bounds = overlay.getBoundingClientRect();
    if (bounds.width <= 0 || bounds.height <= 0) return null;
    const x = ((clientX - bounds.left) / bounds.width) * geometry.width;
    const y = ((clientY - bounds.top) / bounds.height) * geometry.height;
    return {
      x: Math.min(geometry.width, Math.max(0, x)),
      y: Math.min(geometry.height, Math.max(0, y)),
    };
  };

  const boxFromCorners = (a: { x: number; y: number }, b: { x: number; y: number }): RedactionBox => ({
    page,
    x0: Math.min(a.x, b.x),
    y0: Math.min(a.y, b.y),
    x1: Math.max(a.x, b.x),
    y1: Math.max(a.y, b.y),
  });

  const startDraw = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    if ((event.target as HTMLElement).closest('button')) return;
    const origin = pointOnPage(event.clientX, event.clientY);
    if (!origin) return;
    event.preventDefault();
    dragOrigin.current = origin;
    setDraftBox(boxFromCorners(origin, origin));
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const moveDraw = (event: PointerEvent<HTMLDivElement>) => {
    if (!dragOrigin.current) return;
    const current = pointOnPage(event.clientX, event.clientY);
    if (!current) return;
    setDraftBox(boxFromCorners(dragOrigin.current, current));
  };

  const endDraw = (event: PointerEvent<HTMLDivElement>) => {
    if (!dragOrigin.current) return;
    const current = pointOnPage(event.clientX, event.clientY);
    const origin = dragOrigin.current;
    dragOrigin.current = null;
    setDraftBox(null);
    if (overlayRef.current?.hasPointerCapture(event.pointerId)) {
      overlayRef.current.releasePointerCapture(event.pointerId);
    }
    if (!current) return;
    const box = boxFromCorners(origin, current);
    if (box.x1 - box.x0 < MIN_BOX_POINTS || box.y1 - box.y0 < MIN_BOX_POINTS) {
      return;
    }
    onDrawBox(box);
  };

  const goToEnteredPage = () => {
    const parsed = Number.parseInt(displayedPage, 10);
    setPageDraft(null);
    if (!Number.isFinite(parsed) || lastPage < 1) return;
    const next = Math.min(lastPage, Math.max(1, parsed)) - 1;
    if (next !== page) onPageChange(next);
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center justify-between gap-3 pb-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 0}
            className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Previous page"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <form
            className="flex items-center gap-1.5"
            onSubmit={(event) => {
              event.preventDefault();
              goToEnteredPage();
            }}
          >
            <label htmlFor="redaction-go-to-page" className="text-sm text-gray-500">
              Go to page
            </label>
            <input
              id="redaction-go-to-page"
              type="number"
              min={1}
              max={lastPage}
              inputMode="numeric"
              value={displayedPage}
              disabled={lastPage <= 1}
              onChange={(event) => setPageDraft(event.target.value)}
              onBlur={() => setPageDraft(null)}
              className="w-14 rounded-lg border border-gray-200 bg-white px-1.5 py-1 text-center text-sm font-semibold tabular-nums text-gray-700 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 disabled:opacity-40 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
            <span className="text-sm text-gray-500 tabular-nums">of {lastPage}</span>
            <button
              type="submit"
              disabled={lastPage <= 1}
              onMouseDown={(event) => event.preventDefault()}
              className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Go
            </button>
          </form>
          <button
            type="button"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= lastPage - 1}
            className="p-1.5 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Next page"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <Eye className="w-3.5 h-3.5" />
          Hover a box to see under it. Drag to draw one.
        </div>
      </div>

      {hasImageContent && (
        <div className="mb-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            {wasOcrd
              ? 'This page contains an image. Text in it was read automatically, but a face or signature cannot be detected, so draw a box over anything the search missed.'
              : 'This page contains an image. Text inside an image is not found by searching, and a face or signature cannot be detected at all, so draw a box over anything that should be withheld.'}
          </span>
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-auto rounded-xl border border-gray-200 bg-gray-100 p-4">
        {isLoading && (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
          </div>
        )}

        {!isLoading && hasError && (
          <div className="flex h-64 items-center justify-center text-sm text-gray-500">
            This page could not be rendered.
          </div>
        )}

        {!isLoading && !hasError && imageUrl && geometry && (
          <div
            ref={overlayRef}
            className="relative mx-auto w-full max-w-3xl shadow-sm touch-none select-none"
            onPointerDown={startDraw}
            onPointerMove={moveDraw}
            onPointerUp={endDraw}
            onPointerCancel={endDraw}
          >
            <img
              src={imageUrl}
              alt={`Page ${page + 1}`}
              draggable={false}
              className="pointer-events-none block w-full rounded-sm bg-white"
            />
            {pageBoxes.map((box, index) => (
              <OverlayBox
                key={`candidate-${box.page}-${box.x0}-${box.y0}-${index}`}
                box={box}
                geometry={geometry}
                className="bg-gray-900 opacity-100 hover:opacity-10 focus:opacity-10"
                label="Proposed redaction. Hover or focus to reveal the content underneath."
              />
            ))}
            {pageDrawn.map((box, index) => (
              <OverlayBox
                key={`drawn-${box.page}-${box.x0}-${box.y0}-${index}`}
                box={box}
                geometry={geometry}
                className="bg-emerald-900/90 ring-2 ring-emerald-400 opacity-100 hover:opacity-10 focus:opacity-10"
                label="Drawn redaction. Hover or focus to reveal the content underneath."
              />
            ))}
            {draftBox && (
              <div
                className="pointer-events-none absolute border-2 border-dashed border-emerald-500 bg-emerald-500/20"
                style={boxStyle(draftBox, geometry)}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function OverlayBox({
  box,
  geometry,
  className,
  label,
}: {
  box: RedactionBox;
  geometry: RedactionPageGeometry;
  className: string;
  label: string;
}) {
  return (
    <button
      type="button"
      style={boxStyle(box, geometry)}
      className={`absolute focus:outline-none focus:ring-2 focus:ring-emerald-500 ${className}`}
      aria-label={label}
    />
  );
}

function boxStyle(box: RedactionBox, geometry: RedactionPageGeometry) {
  return {
    left: `${(box.x0 / geometry.width) * 100}%`,
    top: `${(box.y0 / geometry.height) * 100}%`,
    width: `${((box.x1 - box.x0) / geometry.width) * 100}%`,
    height: `${((box.y1 - box.y0) / geometry.height) * 100}%`,
  };
}
